'use client';

import { cn } from '@/lib/utils';

interface FluidOrbProps {
    size?: 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
    speed?: 'slow' | 'normal' | 'fast';
    colors?: {
        primary?: string;
        secondary?: string;
        tertiary?: string;
        glow?: string;
    };
}

const sizeClasses = {
    sm: 'w-32 h-32',
    md: 'w-48 h-48',
    lg: 'w-64 h-64',
    xl: 'w-80 h-80',
};

export function FluidOrb({ 
    size = 'lg', 
    className,
    speed = 'normal',
    colors = {
        primary: '#5f33fa',
        secondary: '#fa3387',
        tertiary: '#7c5dfa',
        glow: '#ffffff'
    }
}: FluidOrbProps) {
    const speedClass = speed === 'fast' ? 'orb-fast' : speed === 'slow' ? 'orb-slow' : '';
    
    return (
        <div className={cn('relative flex items-center justify-center overflow-visible', sizeClasses[size], className)}>
            <div className="absolute inset-0 flex items-center justify-center">
                <div 
                    className="w-1/2 h-1/2 rounded-full blur-[160px]" 
                    style={{
                        background: `linear-gradient(to bottom right, ${colors.primary}33, ${colors.secondary}1a)`
                    }}
                />
            </div>
            <svg
                className={cn('w-full h-full relative z-10', speedClass)}
                viewBox="0 0 1200 1200"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <filter id="blob-glow">
                        <feGaussianBlur stdDeviation="16" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                <g className="blob blob-1">
                    <path
                        fill={colors.primary}
                        opacity="0.7"
                        filter="url(#blob-glow)"
                        style={{ filter: 'blur(16px)' }}
                    />
                </g>

                <g className="blob blob-2">
                    <path
                        fill={colors.secondary}
                        opacity="0.7"
                        filter="url(#blob-glow)"
                        style={{ filter: 'blur(12px)' }}
                    />
                </g>

                <g className="blob blob-3">
                    <path
                        fill={colors.tertiary}
                        opacity="0.7"
                        filter="url(#blob-glow)"
                        style={{ filter: 'blur(8px)' }}
                    />
                </g>

                <g className="blob blob-4">
                    <path
                        fill={colors.glow}
                        opacity="0.9"
                        filter="url(#blob-glow)"
                        style={{ filter: 'blur(160px)' }}
                    />
                </g>

                <g className="blob blob-1 alt">
                    <path
                        fill={colors.primary}
                        opacity="0.3"
                        filter="url(#blob-glow)"
                        style={{ filter: 'blur(16px)' }}
                    />
                </g>

                <g className="blob blob-2 alt">
                    <path
                        fill={colors.secondary}
                        opacity="0.3"
                        filter="url(#blob-glow)"
                        style={{ filter: 'blur(12px)' }}
                    />
                </g>

                <g className="blob blob-3 alt">
                    <path
                        fill={colors.tertiary}
                        opacity="0.3"
                        filter="url(#blob-glow)"
                        style={{ filter: 'blur(8px)' }}
                    />
                </g>

                <g className="blob blob-4 alt">
                    <path
                        fill={colors.glow}
                        opacity="0.8"
                        filter="url(#blob-glow)"
                        style={{ filter: 'blur(160px)' }}
                    />
                </g>
            </svg>
        </div>
    );
}

